import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["progress", "heroCta", "mobileCta", "nav", "navLink", "drawer"]

  connect() {
    this.setupScrollProgress()
    this.setupRevealObserver()
    this.setupMobileCta()
    this.setupNavShadow()
    this.setupScrollSpy()
    this.setupAnchorScroll()
  }

  disconnect() {
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler)
    }
    this.revealObserver?.disconnect()
    this.ctaObserver?.disconnect()
    this.spyObserver?.disconnect()
    if (this.anchorNodes && this.anchorClickHandler) {
      this.anchorNodes.forEach(a => a.removeEventListener("click", this.anchorClickHandler))
    }
    if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler)
    }
    document.body.style.overflow = ""
  }

  setupScrollProgress() {
    this.scrollHandler = () => {
      const h = document.documentElement
      const pct = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100
      if (this.hasProgressTarget) {
        this.progressTarget.style.width = `${pct}%`
      }
      // Clear nav-link active state when we're above all tracked sections
      if (this.hasNavLinkTarget && h.scrollTop < 160) {
        this.navLinkTargets.forEach(l => l.classList.remove("is-active"))
      }
      if (this.hasNavTarget) {
        this.navTarget.classList.toggle("scrolled", h.scrollTop > 8)
      }
    }
    window.addEventListener("scroll", this.scrollHandler, { passive: true })
  }

  setupNavShadow() {
    if (this.hasNavTarget && document.documentElement.scrollTop > 8) {
      this.navTarget.classList.add("scrolled")
    }
  }

  setupRevealObserver() {
    this.revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("show") })
    }, { threshold: 0.1, rootMargin: "0px 0px -40px 0px" })

    this.element.querySelectorAll(".reveal, .reveal-left, .reveal-right")
      .forEach(el => this.revealObserver.observe(el))
  }

  setupMobileCta() {
    if (!this.hasHeroCtaTarget || !this.hasMobileCtaTarget) return
    this.ctaObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        this.mobileCtaTarget.classList.toggle("visible", !e.isIntersecting)
      })
    }, { threshold: 0 })
    this.ctaObserver.observe(this.heroCtaTarget)
  }

  setupScrollSpy() {
    if (!this.hasNavLinkTarget) return

    const pairs = this.navLinkTargets
      .map(link => ({ link, section: document.getElementById(link.dataset.sectionId) }))
      .filter(p => p.section)

    if (pairs.length === 0) return

    this.spyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const match = pairs.find(p => p.section === entry.target)
        if (!match) return
        if (entry.isIntersecting) {
          this.navLinkTargets.forEach(l => l.classList.remove("is-active"))
          match.link.classList.add("is-active")
        }
      })
    }, { rootMargin: "-45% 0px -50% 0px", threshold: 0 })

    pairs.forEach(p => this.spyObserver.observe(p.section))
  }

  setupAnchorScroll() {
    this.anchorClickHandler = (e) => {
      const link = e.currentTarget
      const href = link.getAttribute("href")
      if (!href || href.length < 2 || !href.startsWith("#")) return
      const target = document.querySelector(href)
      if (!target) return
      e.preventDefault()
      const offset = 96
      const y = target.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top: y, behavior: "smooth" })
      if (history.pushState) history.pushState(null, "", href)
    }
    this.anchorNodes = Array.from(this.element.querySelectorAll('a[href^="#"]'))
    this.anchorNodes.forEach(link => link.addEventListener("click", this.anchorClickHandler))
  }

  openMenu() {
    if (!this.hasDrawerTarget) return
    this.drawerTarget.classList.add("is-open")
    this.drawerTarget.removeAttribute("inert")
    document.body.style.overflow = "hidden"
    this.escHandler = (e) => { if (e.key === "Escape") this.closeMenu() }
    document.addEventListener("keydown", this.escHandler)
  }

  closeMenu() {
    if (!this.hasDrawerTarget) return
    this.drawerTarget.classList.remove("is-open")
    this.drawerTarget.setAttribute("inert", "")
    document.body.style.overflow = ""
    if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler)
      this.escHandler = null
    }
  }
}
